import {
  DEFAULT_SIDEBAR,
  SCENE_CONTEXTS_SIDEBAR_TAB,
  randomId,
} from "@excalidraw/common";

import {
  CaptureUpdateAction,
  NO_SCENE_CONTEXT_ID,
  SCENE_CONTEXT_COLORS,
  getSceneContextId,
  getSceneContextKey,
  getSceneContextVisibilityKeys,
  getUnknownSceneContextIds,
  newElementWithSceneContextId,
  toggleSceneContextVisibility,
} from "@excalidraw/element";

import type { SceneContext } from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import {
  clockIcon,
  PlusIcon,
  sceneContextFocusIcon,
  sceneContextsIcon,
} from "../components/icons";
import { t } from "../i18n";

import { register } from "./register";

import type { AppClassProperties, AppState, UIAppState } from "../types";

// -----------------------------------------------------------------------------
// helpers
// -----------------------------------------------------------------------------

const getNextSceneContextColor = (sceneContexts: readonly SceneContext[]) =>
  SCENE_CONTEXT_COLORS[sceneContexts.length % SCENE_CONTEXT_COLORS.length];

const getNextSceneContextName = (sceneContexts: readonly SceneContext[]) => {
  const names = new Set(sceneContexts.map((context) => context.name));
  let number = sceneContexts.length + 1;
  let name = t("sceneContexts.defaultName", { number });
  while (names.has(name)) {
    number++;
    name = t("sceneContexts.defaultName", { number });
  }
  return name;
};

/** `appState` without `key` hidden */
const unhide = (
  hiddenIds: AppState["hiddenSceneContextIds"],
  key: string,
): AppState["hiddenSceneContextIds"] => {
  if (!hiddenIds[key]) {
    return hiddenIds;
  }
  const next = { ...hiddenIds };
  delete next[key];
  return next;
};

/**
 * Updates a registry entry. An id missing from the registry (an "Unknown
 * context" row) is added under the same id.
 */
const upsertSceneContext = (
  sceneContexts: readonly SceneContext[],
  id: string,
  updates: Partial<Omit<SceneContext, "id">>,
): readonly SceneContext[] => {
  if (sceneContexts.some((context) => context.id === id)) {
    return sceneContexts.map((context) =>
      context.id === id ? { ...context, ...updates } : context,
    );
  }
  return [
    ...sceneContexts,
    {
      id,
      name: updates.name ?? "",
      color: updates.color ?? getNextSceneContextColor(sceneContexts),
    },
  ];
};

/**
 * The selected elements plus the bound text of selected containers (bound
 * text follows its container).
 */
const getElementsToMove = (
  appState: Pick<AppState, "selectedElementIds">,
  app: AppClassProperties,
) =>
  app.scene.getSelectedElements({
    selectedElementIds: appState.selectedElementIds,
    includeBoundTextElement: true,
  });

const reassignSceneContext = (
  elements: readonly ExcalidrawElement[],
  shouldReassign: (element: ExcalidrawElement) => boolean,
  targetId: string | null,
) => {
  let didChange = false;
  const nextElements = elements.map((element) => {
    if (!shouldReassign(element)) {
      return element;
    }
    const nextElement = newElementWithSceneContextId(element, targetId);
    if (nextElement !== element) {
      didChange = true;
    }
    return nextElement;
  });
  return { nextElements, didChange };
};

/** a context's display name ("Unknown context" for ids missing locally) */
export const getSceneContextDisplayName = (
  id: string | null,
  sceneContexts: UIAppState["sceneContexts"],
) => {
  if (!id || id === NO_SCENE_CONTEXT_ID) {
    return t("sceneContexts.noContext");
  }
  const sceneContext = sceneContexts.find((context) => context.id === id);
  if (!sceneContext) {
    return t("sceneContexts.unknownContext");
  }
  return sceneContext.name.trim() || t("sceneContexts.untitled");
};

// -----------------------------------------------------------------------------
// view state
// -----------------------------------------------------------------------------

export const actionToggleSceneContextMode = register({
  name: "toggleSceneContextMode",
  label: "sceneContexts.toggleMode",
  icon: sceneContextsIcon,
  keywords: ["context", "tag", "layer"],
  viewMode: false,
  trackEvent: { category: "canvas" },
  perform(elements, appState) {
    const sceneContextModeEnabled = !appState.sceneContextModeEnabled;
    // turning the mode on without an active context activates the first one
    const activeSceneContextId =
      sceneContextModeEnabled &&
      !appState.activeSceneContextId &&
      appState.sceneContexts.length
        ? appState.sceneContexts[0].id
        : appState.activeSceneContextId;
    return {
      appState: {
        ...appState,
        sceneContextModeEnabled,
        activeSceneContextId,
        hiddenSceneContextIds:
          sceneContextModeEnabled && activeSceneContextId
            ? unhide(appState.hiddenSceneContextIds, activeSceneContextId)
            : appState.hiddenSceneContextIds,
      },
      captureUpdate: CaptureUpdateAction.EVENTUALLY,
    };
  },
  checked: (appState) => appState.sceneContextModeEnabled,
});

/**
 * Context focus: elements outside the active context are dimmed (still
 * visible and interactive).
 */
export const actionToggleSceneContextFocus = register({
  name: "toggleSceneContextFocus",
  label: "sceneContexts.toggleFocus",
  icon: sceneContextFocusIcon,
  keywords: ["context", "focus", "dim", "highlight"],
  viewMode: true,
  trackEvent: { category: "canvas" },
  perform(elements, appState) {
    return {
      appState: {
        ...appState,
        sceneContextFocusEnabled: !appState.sceneContextFocusEnabled,
      },
      captureUpdate: CaptureUpdateAction.EVENTUALLY,
    };
  },
  checked: (appState) => appState.sceneContextFocusEnabled,
});

export const actionToggleElementTimestamps = register({
  name: "toggleElementTimestamps",
  label: "sceneContexts.toggleTimestamps",
  icon: clockIcon,
  keywords: ["time", "date", "created", "edited", "modified", "timestamp"],
  viewMode: true,
  trackEvent: { category: "canvas" },
  perform(elements, appState) {
    return {
      appState: {
        ...appState,
        showElementTimestamps: !appState.showElementTimestamps,
      },
      captureUpdate: CaptureUpdateAction.EVENTUALLY,
    };
  },
  checked: (appState) => appState.showElementTimestamps,
});

export const actionToggleSceneContextsPanel = register({
  name: "toggleSceneContextsPanel",
  label: "sceneContexts.openPanel",
  icon: sceneContextsIcon,
  keywords: ["context", "contexts", "tag", "layers", "sidebar"],
  viewMode: true,
  trackEvent: { category: "menu" },
  perform(elements, appState) {
    const isOpen =
      appState.openSidebar?.name === DEFAULT_SIDEBAR.name &&
      appState.openSidebar.tab === SCENE_CONTEXTS_SIDEBAR_TAB;
    return {
      appState: {
        ...appState,
        openSidebar: isOpen
          ? null
          : { name: DEFAULT_SIDEBAR.name, tab: SCENE_CONTEXTS_SIDEBAR_TAB },
        openDialog: null,
      },
      captureUpdate: CaptureUpdateAction.EVENTUALLY,
    };
  },
});

export const actionSetActiveSceneContext = register<{
  /** `null` / `NO_SCENE_CONTEXT_ID` for "No context" */
  id: string | null;
  /** also turn context mode on (when activating an actual context) */
  enableMode?: boolean;
}>({
  name: "setActiveSceneContext",
  label: "sceneContexts.setActive",
  viewMode: false,
  trackEvent: false,
  perform(elements, appState, data) {
    if (!data) {
      return false;
    }
    const activeSceneContextId =
      data.id === NO_SCENE_CONTEXT_ID ? null : data.id;
    return {
      appState: {
        ...appState,
        activeSceneContextId,
        sceneContextModeEnabled:
          (!!data.enableMode && !!activeSceneContextId) ||
          appState.sceneContextModeEnabled,
        // activating a context un-hides it
        hiddenSceneContextIds: unhide(
          appState.hiddenSceneContextIds,
          getSceneContextKey(activeSceneContextId),
        ),
      },
      captureUpdate: CaptureUpdateAction.EVENTUALLY,
    };
  },
});

export const actionToggleSceneContextVisibility = register<{
  /** context id or `NO_SCENE_CONTEXT_ID` */
  key: string;
  /** hide every other row (or show all if `key` is the only visible one) */
  solo?: boolean;
}>({
  name: "toggleSceneContextVisibility",
  label: "sceneContexts.toggleVisibility",
  viewMode: true,
  trackEvent: false,
  perform(elements, appState, data) {
    if (!data) {
      return false;
    }
    return {
      appState: {
        ...appState,
        hiddenSceneContextIds: toggleSceneContextVisibility(
          appState.hiddenSceneContextIds,
          data.key,
          getSceneContextVisibilityKeys(elements, appState.sceneContexts),
          data.solo,
        ),
      },
      captureUpdate: CaptureUpdateAction.EVENTUALLY,
    };
  },
});

// -----------------------------------------------------------------------------
// registry (outside undo history: the registry isn't part of the observed
// app state)
// -----------------------------------------------------------------------------

export const actionAddSceneContext = register<{
  name?: string;
  color?: string;
}>({
  name: "addSceneContext",
  label: "sceneContexts.add",
  icon: PlusIcon,
  keywords: ["context", "new", "create", "tag"],
  viewMode: false,
  trackEvent: { category: "canvas" },
  perform(elements, appState, data) {
    const sceneContext: SceneContext = {
      id: randomId(),
      name:
        data?.name?.trim() || getNextSceneContextName(appState.sceneContexts),
      color: data?.color || getNextSceneContextColor(appState.sceneContexts),
    };
    return {
      appState: {
        ...appState,
        sceneContexts: [...appState.sceneContexts, sceneContext],
        activeSceneContextId: sceneContext.id,
        // from the command palette (no data), reveal the new row
        openSidebar: data
          ? appState.openSidebar
          : { name: DEFAULT_SIDEBAR.name, tab: SCENE_CONTEXTS_SIDEBAR_TAB },
      },
      captureUpdate: CaptureUpdateAction.EVENTUALLY,
    };
  },
});

export const actionRenameSceneContext = register<{
  id: string;
  name: string;
}>({
  name: "renameSceneContext",
  label: "sceneContexts.rename",
  viewMode: false,
  trackEvent: false,
  perform(elements, appState, data) {
    if (!data || data.id === NO_SCENE_CONTEXT_ID) {
      return false;
    }
    return {
      appState: {
        ...appState,
        sceneContexts: upsertSceneContext(appState.sceneContexts, data.id, {
          name: data.name.trim(),
        }),
      },
      captureUpdate: CaptureUpdateAction.EVENTUALLY,
    };
  },
});

export const actionChangeSceneContextColor = register<{
  id: string;
  color: string;
}>({
  name: "changeSceneContextColor",
  label: "sceneContexts.changeColor",
  viewMode: false,
  trackEvent: false,
  perform(elements, appState, data) {
    if (!data || data.id === NO_SCENE_CONTEXT_ID || !data.color) {
      return false;
    }
    return {
      appState: {
        ...appState,
        sceneContexts: upsertSceneContext(appState.sceneContexts, data.id, {
          color: data.color,
        }),
      },
      captureUpdate: CaptureUpdateAction.EVENTUALLY,
    };
  },
});

/**
 * Removes a context. Its elements are reassigned to `targetId` (another
 * context, or `null` for "No context") in one undoable step; the registry
 * change itself is outside history. Without data, opens the dialog asking
 * where the elements go.
 */
export const actionDeleteSceneContext = register<{
  id: string;
  targetId?: string | null;
  /** only open the dialog asking for `targetId` */
  confirm?: boolean;
}>({
  name: "deleteSceneContext",
  label: "sceneContexts.delete",
  viewMode: false,
  trackEvent: { category: "canvas" },
  perform(elements, appState, data) {
    if (!data || data.id === NO_SCENE_CONTEXT_ID) {
      return false;
    }
    if (data.confirm) {
      return {
        appState: {
          ...appState,
          openDialog: { name: "deleteSceneContext", sceneContextId: data.id },
        },
        captureUpdate: CaptureUpdateAction.EVENTUALLY,
      };
    }

    const targetId =
      data.targetId && data.targetId !== data.id ? data.targetId : null;

    const { nextElements, didChange } = reassignSceneContext(
      elements,
      (element) => getSceneContextId(element) === data.id,
      targetId,
    );

    const hiddenSceneContextIds = { ...appState.hiddenSceneContextIds };
    delete hiddenSceneContextIds[data.id];

    return {
      elements: didChange ? nextElements : undefined,
      appState: {
        ...appState,
        sceneContexts: appState.sceneContexts.filter(
          (context) => context.id !== data.id,
        ),
        activeSceneContextId:
          appState.activeSceneContextId === data.id
            ? null
            : appState.activeSceneContextId,
        hiddenSceneContextIds,
        openDialog:
          appState.openDialog?.name === "deleteSceneContext"
            ? null
            : appState.openDialog,
      },
      captureUpdate: didChange
        ? CaptureUpdateAction.IMMEDIATELY
        : CaptureUpdateAction.EVENTUALLY,
    };
  },
});

// -----------------------------------------------------------------------------
// selection
// -----------------------------------------------------------------------------

/**
 * Reassigns the selection (bound text follows its container) to a context id,
 * or to "No context". One undoable step. Without data (command palette,
 * context menu), opens the context picker.
 */
export const actionMoveSelectionToSceneContext = register<{
  /** `null` / `NO_SCENE_CONTEXT_ID` for "No context" */
  id: string | null;
}>({
  name: "moveSelectionToSceneContext",
  label: "sceneContexts.moveToEllipsis",
  icon: sceneContextsIcon,
  keywords: ["context", "tag", "assign", "move"],
  viewMode: false,
  trackEvent: { category: "element" },
  predicate: (elements, appState, _, app) =>
    app.scene.getSelectedElements(appState).length > 0,
  perform(elements, appState, data, app) {
    if (!data) {
      return {
        appState: {
          ...appState,
          openDialog: { name: "moveToSceneContext" },
        },
        captureUpdate: CaptureUpdateAction.EVENTUALLY,
      };
    }
    const targetId = data.id === NO_SCENE_CONTEXT_ID ? null : data.id;
    const toMove = new Set(
      getElementsToMove(appState, app).map((element) => element.id),
    );
    if (!toMove.size) {
      return false;
    }
    const { nextElements, didChange } = reassignSceneContext(
      elements,
      (element) => toMove.has(element.id),
      targetId,
    );
    return {
      elements: didChange ? nextElements : undefined,
      appState: {
        ...appState,
        openDialog:
          appState.openDialog?.name === "moveToSceneContext"
            ? null
            : appState.openDialog,
      },
      captureUpdate: didChange
        ? CaptureUpdateAction.IMMEDIATELY
        : CaptureUpdateAction.EVENTUALLY,
    };
  },
  PanelComponent: ({ elements, appState, updateData, app }) => {
    const unknownIds = getUnknownSceneContextIds(
      elements,
      appState.sceneContexts,
    );
    if (!appState.sceneContexts.length && !unknownIds.length) {
      return null;
    }
    const selected = app.scene
      .getSelectedElements(appState)
      .filter((element) => !element.isDeleted);
    const ids = new Set(selected.map((element) => getSceneContextId(element)));
    const value =
      ids.size === 1
        ? getSceneContextKey([...ids][0])
        : // mixed selection
          "";

    return (
      <fieldset className="scene-context-picker">
        <legend>{t("sceneContexts.context")}</legend>
        <select
          className="scene-context-picker__select"
          aria-label={t("sceneContexts.moveTo")}
          title={t("sceneContexts.moveTo")}
          value={value}
          onChange={(event) => updateData({ id: event.target.value })}
        >
          {value === "" && <option value="" disabled />}
          {appState.sceneContexts.map((sceneContext) => (
            <option key={sceneContext.id} value={sceneContext.id}>
              {getSceneContextDisplayName(
                sceneContext.id,
                appState.sceneContexts,
              )}
            </option>
          ))}
          {unknownIds.map((id) => (
            <option key={id} value={id}>
              {`${t("sceneContexts.unknownContext")} (${id.slice(0, 6)})`}
            </option>
          ))}
          <option value={NO_SCENE_CONTEXT_ID}>
            {t("sceneContexts.noContext")}
          </option>
        </select>
      </fieldset>
    );
  },
});
